import React, { useState } from 'react';
import {
  Video,
  TrendingUp,
  Clock,
  Star,
  Search,
  Play,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  BookOpen,
  Wifi,
  Brain,
  CheckCircle,
  User,
  Monitor
} from 'lucide-react';

const TradingViewCoursesWireframe = () => {
  const [viewMode, setViewMode] = useState('desktop'); // 'desktop' or 'mobile'
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Sample course data
  const courses = [
    {
      id: 1,
      title: 'TradingView Platform Essentials',
      description: 'Learn the fundamentals of TradingView platform, navigation, and basic charting tools',
      thumbnail: 'https://via.placeholder.com/400x225/30C4B5/ffffff?text=TradingView+Basics',
      category: 'TradingView',
      lessons: 5,
      duration: '2.5h',
      rating: 4.8,
      instructor: 'John Trader',
      enrolled: true,
      progress: 65
    },
    {
      id: 2,
      title: 'Advanced Chart Analysis with TradingView',
      description: 'Master advanced charting techniques, multi-timeframe analysis, and professional setups',
      thumbnail: 'https://via.placeholder.com/400x225/3B82F6/ffffff?text=Chart+Analysis',
      category: 'TradingView',
      lessons: 8,
      duration: '4.2h',
      rating: 4.9,
      instructor: 'Sarah Charts',
      enrolled: false,
      progress: 0
    },
    {
      id: 3,
      title: 'Pine Script for Beginners',
      description: 'Introduction to Pine Script programming language for creating custom indicators',
      thumbnail: 'https://via.placeholder.com/400x225/10B981/ffffff?text=Pine+Script',
      category: 'TradingView',
      lessons: 12,
      duration: '6.5h',
      rating: 4.7,
      instructor: 'Mike Code',
      enrolled: true,
      progress: 30
    },
    {
      id: 4,
      title: 'TradingView Alerts & Automation',
      description: 'Set up powerful alerts and notifications to automate your trading workflow',
      thumbnail: 'https://via.placeholder.com/400x225/F59E0B/ffffff?text=Alerts',
      category: 'TradingView',
      lessons: 4,
      duration: '2.0h',
      rating: 4.6,
      instructor: 'Lisa Alert',
      enrolled: false,
      progress: 0
    },
    {
      id: 5,
      title: 'Drawing Tools Mastery',
      description: 'Complete guide to all TradingView drawing tools for technical analysis',
      thumbnail: 'https://via.placeholder.com/400x225/8B5CF6/ffffff?text=Drawing+Tools',
      category: 'TradingView',
      lessons: 6,
      duration: '3.5h',
      rating: 4.8,
      instructor: 'Tom Draw',
      enrolled: true,
      progress: 85
    },
    {
      id: 6,
      title: 'Strategy Testing & Backtesting',
      description: 'Learn how to test and validate your trading strategies using TradingView tools',
      thumbnail: 'https://via.placeholder.com/400x225/EF4444/ffffff?text=Strategy+Testing',
      category: 'TradingView',
      lessons: 10,
      duration: '5.0h',
      rating: 4.9,
      instructor: 'David Strategy',
      enrolled: false,
      progress: 0
    }
  ];

  const stats = [
    { label: 'Total Courses', value: '24', icon: Video, color: 'bg-teal-100 text-teal-600' },
    { label: 'Enrolled', value: '8', icon: TrendingUp, color: 'bg-green-100 text-green-600' },
    { label: 'Total Content', value: '45.5h', icon: Clock, color: 'bg-blue-100 text-blue-600' },
    { label: 'Avg Rating', value: '4.8', icon: Star, color: 'bg-yellow-100 text-yellow-600' }
  ];

  const topics = [
    'Platform Basics',
    'Chart Analysis',
    'Pine Script',
    'Indicators',
    'Drawing Tools',
    'Alerts & Notifications',
    'Advanced Features',
    'Strategy Testing'
  ];

  const Sidebar = () => (
    <div className={`${viewMode === 'mobile' && !sidebarOpen ? 'hidden' : 'block'} ${viewMode === 'desktop' ? 'w-64' : 'fixed inset-0 z-50 w-64'} bg-[#001D2E] text-white flex flex-col`}>
      {/* Logo */}
      <div className="p-6 border-b border-gray-700">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">Learn To Trade</h1>
          {viewMode === 'mobile' && (
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden">
              <X size={24} />
            </button>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 overflow-y-auto">
        <ul className="space-y-2">
          {/* Dashboard */}
          <li>
            <a href="#" className="flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-gray-700 transition">
              <Monitor size={20} />
              <span>Dashboard</span>
            </a>
          </li>

          {/* My Courses */}
          <li>
            <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-gray-700">
              <BookOpen size={20} />
              <span>My Courses</span>
              <ChevronDown size={16} className="ml-auto" />
            </div>
            <ul className="ml-4 mt-2 space-y-1">
              <li>
                <a href="#" className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg hover:bg-gray-700 transition">
                  All My Courses
                </a>
              </li>
              <li>
                <div className="flex items-center gap-2 px-4 py-2 text-sm">
                  Ebooks
                  <ChevronRight size={14} className="ml-auto" />
                </div>
                <ul className="ml-4 space-y-1">
                  <li><a href="#" className="block px-4 py-1 text-sm text-gray-400 hover:text-white transition">Basic</a></li>
                  <li><a href="#" className="block px-4 py-1 text-sm text-gray-400 hover:text-white transition">Intermediate</a></li>
                  <li><a href="#" className="block px-4 py-1 text-sm text-gray-400 hover:text-white transition">Advanced</a></li>
                </ul>
              </li>
              <li>
                <div className="flex items-center gap-2 px-4 py-2 text-sm">
                  Video Courses
                  <ChevronDown size={14} className="ml-auto" />
                </div>
                <ul className="ml-4 space-y-1">
                  <li>
                    <a href="#" className="flex items-center gap-2 px-4 py-1 text-sm bg-[#30C4B5] rounded-lg">
                      Tradingview
                      <Star size={12} className="ml-auto fill-yellow-400 text-yellow-400" />
                    </a>
                  </li>
                  <li><a href="#" className="block px-4 py-1 text-sm text-gray-400 hover:text-white transition">Indicator</a></li>
                  <li><a href="#" className="block px-4 py-1 text-sm text-gray-400 hover:text-white transition">General</a></li>
                </ul>
              </li>
            </ul>
          </li>

          {/* Live Streaming */}
          <li>
            <a href="#" className="flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-gray-700 transition">
              <Wifi size={20} />
              <span>Live Streaming</span>
            </a>
          </li>

          {/* AI Trading */}
          <li>
            <a href="#" className="flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-gray-700 transition">
              <Brain size={20} />
              <span>AI Trading</span>
            </a>
          </li>
        </ul>
      </nav>
    </div>
  );

  const CourseCard = ({ course }) => (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-200 overflow-hidden group">
      {/* Thumbnail */}
      <div className="relative aspect-video overflow-hidden bg-gray-200">
        <img
          src={course.thumbnail}
          alt={course.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
        />

        {/* Category Badge */}
        <div className="absolute top-3 left-3 bg-[#30C4B5] text-white text-xs font-semibold px-3 py-1 rounded-full">
          {course.category}
        </div>

        {/* Rating Badge */}
        <div className="absolute top-3 right-3 bg-yellow-400 text-gray-900 text-xs font-semibold px-2 py-1 rounded-full flex items-center gap-1">
          <Star size={12} className="fill-gray-900" />
          {course.rating}
        </div>

        {/* Play Button Overlay */}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <div className="w-16 h-16 bg-[#30C4B5] rounded-full flex items-center justify-center">
            <Play size={28} className="fill-white text-white ml-1" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        {/* Badges */}
        <div className="flex gap-2 mb-3">
          <span className="bg-green-100 text-green-700 text-xs font-medium px-2.5 py-1 rounded">
            Video Course
          </span>
          <span className="bg-blue-100 text-blue-700 text-xs font-medium px-2.5 py-1 rounded">
            {course.lessons} lessons
          </span>
        </div>

        {/* Title */}
        <h3 className="font-bold text-gray-900 text-lg mb-2 line-clamp-2">
          {course.title}
        </h3>

        {/* Description */}
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {course.description}
        </p>

        {/* Meta Info */}
        <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
          <div className="flex items-center gap-1">
            <User size={14} />
            {course.instructor}
          </div>
          <div className="flex items-center gap-1">
            <Clock size={14} />
            {course.duration}
          </div>
          <div className="flex items-center gap-1">
            <Monitor size={14} />
            HD
          </div>
        </div>

        {/* Progress Bar (if enrolled) */}
        {course.enrolled && (
          <div className="mb-4">
            <div className="flex justify-between text-xs text-gray-600 mb-1">
              <span>Progress</span>
              <span>{course.progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-[#30C4B5] h-2 rounded-full transition-all"
                style={{ width: `${course.progress}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Action Button */}
        <button className={`w-full py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
          course.enrolled
            ? 'bg-[#30C4B5] text-white hover:bg-[#26a69a]'
            : 'bg-[#001D2E] text-white hover:bg-[#003D5E]'
        }`}>
          {course.enrolled ? (
            <>
              <Play size={18} />
              Continue Learning
            </>
          ) : (
            <>
              <CheckCircle size={18} />
              Enroll Now
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* View Mode Toggle */}
      <div className="fixed top-4 right-4 z-50 bg-white rounded-lg shadow-lg p-2 flex gap-2">
        <button
          onClick={() => setViewMode('desktop')}
          className={`px-4 py-2 rounded-md font-medium transition ${
            viewMode === 'desktop'
              ? 'bg-[#30C4B5] text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Desktop
        </button>
        <button
          onClick={() => setViewMode('mobile')}
          className={`px-4 py-2 rounded-md font-medium transition ${
            viewMode === 'mobile'
              ? 'bg-[#30C4B5] text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Mobile
        </button>
      </div>

      {/* Main Layout */}
      <div className={`flex ${viewMode === 'mobile' ? 'max-w-md mx-auto' : ''}`}>
        {/* Sidebar */}
        {viewMode === 'desktop' && <Sidebar />}
        {viewMode === 'mobile' && sidebarOpen && <Sidebar />}

        {/* Mobile Overlay */}
        {viewMode === 'mobile' && sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Mobile Header */}
          {viewMode === 'mobile' && (
            <div className="bg-white border-b border-gray-200 p-4 flex items-center gap-4">
              <button onClick={() => setSidebarOpen(true)}>
                <Menu size={24} />
              </button>
              <h1 className="font-bold text-lg">Learn To Trade</h1>
            </div>
          )}

          <div className="p-6 lg:p-8 max-w-7xl mx-auto">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
              <a href="#" className="hover:text-[#30C4B5]">Dashboard</a>
              <ChevronRight size={16} />
              <a href="#" className="hover:text-[#30C4B5]">Courses</a>
              <ChevronRight size={16} />
              <a href="#" className="hover:text-[#30C4B5]">Video Courses</a>
              <ChevronRight size={16} />
              <span className="text-[#30C4B5] font-medium">TradingView</span>
            </div>

            {/* Header Section */}
            <div className="mb-8">
              <div className="flex items-center gap-4 mb-3">
                <div className="w-16 h-16 bg-[#30C4B5] rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Video size={32} className="text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">TradingView Video Courses</h1>
                  <p className="text-gray-600">Master TradingView platform with comprehensive video tutorials and advanced techniques</p>
                </div>
              </div>
            </div>

            {/* Stats Cards */}
            <div className={`grid ${viewMode === 'mobile' ? 'grid-cols-2' : 'grid-cols-4'} gap-4 mb-8`}>
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div key={index} className="bg-white rounded-xl shadow-sm p-5 flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-lg ${stat.color} flex items-center justify-center flex-shrink-0`}>
                      <Icon size={24} />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                      <div className="text-sm text-gray-600">{stat.label}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Search & Filter */}
            <div className={`${viewMode === 'mobile' ? 'flex-col' : 'flex'} gap-4 mb-6`}>
              <div className="flex-1 relative mb-4 lg:mb-0">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search TradingView courses..."
                  className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#30C4B5] focus:outline-none transition"
                />
              </div>
              <select className="px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#30C4B5] focus:outline-none bg-white cursor-pointer">
                <option>Newest First</option>
                <option>Most Popular</option>
                <option>Shortest First</option>
              </select>
            </div>

            {/* Popular Topics */}
            <div className="mb-8">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">POPULAR TOPICS</h3>
              <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                {topics.map((topic, index) => (
                  <button
                    key={index}
                    className="px-4 py-2 bg-white border-2 border-gray-200 rounded-full text-sm font-medium text-gray-700 hover:border-[#30C4B5] hover:text-[#30C4B5] transition whitespace-nowrap"
                  >
                    {topic}
                  </button>
                ))}
              </div>
            </div>

            {/* Course Grid */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">All TradingView Courses</h2>
              <div className={`grid ${viewMode === 'mobile' ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'} gap-6`}>
                {courses.map((course) => (
                  <CourseCard key={course.id} course={course} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default TradingViewCoursesWireframe;
